import { motion } from "framer-motion";
import type { MenuItem } from "../../types/menu";
import Badge from "../ui/Badge";

interface MenuCardProps {
  item: MenuItem;
  index?: number;
}

export default function MenuCard({ item, index = 0 }: MenuCardProps) {
  const hasImage = Boolean(item.image);
  const priceLabel = item.sizes
    ? `desde $${Math.min(...item.sizes.map((s) => s.price)).toFixed(2)}`
    : `$${item.price!.toFixed(2)}`;

  return (
    <motion.article
      className="group flex flex-col bg-white rounded-3xl overflow-hidden shadow-sm border border-black/[0.04]"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.4,
        delay: index * 0.05,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{
        y: -4,
        boxShadow:
          "0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.06)",
      }}
    >
      {/* Image */}
      {hasImage && (
        <div className="relative aspect-[4/3] overflow-hidden shrink-0">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
            loading="lazy"
          />
          {/* Gradient scrim */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent" />
          {/* Price pill */}
          <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm text-primary font-extrabold text-sm px-3.5 py-1.5 rounded-xl shadow-lg">
            {priceLabel}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col flex-1 p-5 gap-2.5">
        {/* Price shown inline when no image */}
        {!hasImage && (
          <span className="self-end bg-primary/10 text-primary font-extrabold text-sm px-3.5 py-1.5 rounded-xl">
            {priceLabel}
          </span>
        )}
        <h3 className="font-heading font-bold text-lg text-[var(--color-text-base)] leading-snug">
          {item.name}
        </h3>
        {item.tagline && (
          <p className="text-sm font-semibold text-primary leading-snug -mt-1">
            {item.tagline}
          </p>
        )}
        <p className="text-sm text-[var(--color-text-muted)] leading-relaxed line-clamp-3 flex-1">
          {item.description}
        </p>

        {/* Sizes */}
        {item.sizes && item.sizes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {item.sizes.map((size) => (
              <span
                key={size.label}
                className="text-xs font-semibold bg-sand px-2.5 py-1 rounded-lg text-warm-gray"
              >
                {size.label}{" "}
                <span className="text-primary">${size.price.toFixed(2)}</span>
              </span>
            ))}
          </div>
        )}

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {item.tags.map((tag) => (
              <Badge key={tag} tag={tag} />
            ))}
          </div>
        )}
      </div>
    </motion.article>
  );
}
